import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, getArticlesByCategory, type Article } from '@/mocks/articles';

const articleThumbImages: Record<string, string> = {
  'article-entry-guide': 'https://readdy.ai/api/search-image?query=A%20modern%20clean%20digital%20illustration%20of%20a%20secure%20web%20browser%20window%20showing%20a%20platform%20portal%20entrance%20with%20a%20glowing%20entry%20gate%20symbol%2C%20warm%20orange%20and%20teal%20accent%20colors%2C%20minimalist%20flat%20design&width=800&height=500&seq=cat-entry-guide&orientation=landscape',
  'article-registration': 'https://readdy.ai/api/search-image?query=A%20clean%20and%20modern%20digital%20illustration%20of%20a%20user%20registration%20interface%20on%20a%20laptop%20screen%2C%20showing%20a%20friendly%20signup%20form%20with%20soft%20warm%20lighting%2C%20minimalist%20design%20with%20orange%20and%20cream%20tones&width=800&height=500&seq=cat-registration&orientation=landscape',
  'article-features': 'https://readdy.ai/api/search-image?query=A%20modern%20digital%20illustration%20showcasing%20multiple%20service%20platform%20features%20as%20interconnected%20floating%20cards%20with%20icons%2C%20warm%20orange%20and%20teal%20color%20palette%2C%20minimalist%20flat%20design&width=800&height=500&seq=cat-features&orientation=landscape',
  'article-security': 'https://readdy.ai/api/search-image?query=A%20modern%20cybersecurity%20themed%20digital%20illustration%20showing%20a%20shield%20icon%20protecting%20digital%20assets%2C%20warm%20dark%20background%20with%20orange%20and%20teal%20accent%20lighting&width=800&height=500&seq=cat-security&orientation=landscape',
  'article-update': 'https://readdy.ai/api/search-image?query=A%20modern%20digital%20illustration%20showing%20a%20platform%20update%20announcement%20with%20glowing%20notification%20cards%2C%20warm%20orange%20and%20cream%20color%20palette%2C%20minimalist%20flat%20design&width=800&height=500&seq=cat-update&orientation=landscape',
  'article-faq-account': 'https://readdy.ai/api/search-image?query=A%20modern%20customer%20support%20illustration%20with%20a%20friendly%20help%20desk%20scene%2C%20warm%20orange%20and%20cream%20tones%2C%20minimalist%20flat%20design%2C%20question%20mark%20icons&width=800&height=500&seq=cat-faq-account&orientation=landscape',
  'article-comparison': 'https://readdy.ai/api/search-image?query=A%20modern%20comparison%20chart%20illustration%20showing%20multiple%20platform%20feature%20cards%20side%20by%20side%2C%20warm%20orange%20and%20teal%20color%20palette%2C%20minimalist%20flat%20design&width=800&height=500&seq=cat-comparison&orientation=landscape',
  'article-mobile': 'https://readdy.ai/api/search-image?query=A%20modern%20digital%20illustration%20of%20a%20smartphone%20displaying%20a%20clean%20mobile%20app%20interface%20with%20warm%20orange%20accents%2C%20minimalist%20flat%20design%2C%20handheld%20device%20on%20a%20clean%20desk%20setup&width=800&height=500&seq=cat-mobile&orientation=landscape',
  'article-community': 'https://readdy.ai/api/search-image?query=A%20modern%20illustration%20representing%20online%20community%20guidelines%20and%20respectful%20communication%2C%20warm%20orange%20and%20cream%20color%20palette%2C%20minimalist%20flat%20design%2C%20interconnected%20user%20avatars&width=800&height=500&seq=cat-community&orientation=landscape',
  'article-access-faq': 'https://readdy.ai/api/search-image?query=A%20modern%20technical%20troubleshooting%20illustration%20showing%20network%20diagnostics%20with%20glowing%20connection%20nodes%2C%20warm%20orange%20and%20cream%20tones%2C%20minimalist%20flat%20design&width=800&height=500&seq=cat-access-faq&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'guide');
  const [articles, setArticles] = useState<Article[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    setArticles(getArticlesByCategory(activeSlug));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const canonicalUrl = `https://91.cc/category/${activeSlug}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name} - 91.cc`,
    description: activeCategory.description,
    url: canonicalUrl,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: articles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://91.cc/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  const handleCategoryChange = (categorySlug: string) => {
    setActiveSlug(categorySlug);
    navigate(`/category/${categorySlug}`);
  };

  return (
    <Layout>
      <SEO
        title={`${activeCategory.name} - 91.cc 官方平台信息导航`}
        description={activeCategory.description}
        keywords={`91.cc,${activeCategory.name},91.cc使用指南,91.cc功能介绍`}
        canonicalUrl={canonicalUrl}
        jsonLd={jsonLd}
      />

      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: activeCategory.name },
        ]}
      />

      <section className="w-full bg-background-50 py-8 md:py-12" aria-labelledby="category-heading">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h1 id="category-heading" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
            {activeCategory.name}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mb-8">
            {activeCategory.description}
          </p>

          <div className="flex flex-wrap items-center gap-2 mb-10" role="tablist" aria-label="文章分类">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                role="tab"
                aria-selected={activeSlug === cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {articles.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">暂无相关文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={articleThumbImages[article.imageSeq] || articleThumbImages['article-features']}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-block px-2.5 py-0.5 text-xs font-medium rounded-full bg-accent-100 text-accent-700">
                        {article.tags[0]}
                      </span>
                      <span className="text-xs text-foreground-500">{article.publishedAt}</span>
                    </div>
                    <h3 className="text-base font-semibold font-heading text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                    <div className="flex items-center gap-2 mt-4 pt-3 border-t border-background-100">
                      <span className="text-xs text-foreground-500">阅读 {article.readTime} 分钟</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}